import isAdmin from "@/components/helpers/isAdmin"
import {
  useCurrentConstituent,
  useCurrentUser,
  useCustomer,
  useShowAdminView,
} from "@/hooks/reduxHooks"
import { useAppSelector } from "@/store/store"

// List of permissions

function getGroupEmail(customer, currentUser, currentConstituent) {
  return (
    customer.groupEmailEnabled &&
    (!customer.hasChapters || currentUser?.chapters[customer.id]) &&
    !(!currentConstituent.classYear && customer.privacyLimitLocationDataEnabled)
  )
}

export const GROUP_EMAIL_ENABLED = (
  customer,
  currentUser,
  _organizations,
  _showAdminView,
  currentConstituent,
) => getGroupEmail(customer, currentUser, currentConstituent)

export const GROUP_EMAIL_ENABLED_OR_ADMIN = (
  customer,
  currentUser,
  _organizations,
  showAdminView,
  currentConstituent,
) => {
  const currentUserIsAdmin = isAdmin(currentUser, customer, showAdminView)

  return currentUserIsAdmin || getGroupEmail(customer, currentUser, currentConstituent)
}

export const HAS_EMAIL_DRAFTS_INTERNAL = (currentConstituent) => {
  const emailDrafts = currentConstituent?.emailDrafts || []
  return emailDrafts.length > 0
}

export const HAS_EMAIL_DRAFTS = (
  _customer,
  _currentUser,
  _organizations,
  _showAdminView,
  currentConstituent,
) => HAS_EMAIL_DRAFTS_INTERNAL(currentConstituent)

export const GROUP_EMAIL_ENABLED_AND_EMAIL_DRAFTS = (
  customer,
  currentUser,
  organizations,
  showAdminView,
  currentConstituent,
) => {
  const conditionOne = GROUP_EMAIL_ENABLED(
    customer,
    currentUser,
    organizations,
    showAdminView,
    currentConstituent,
  )
  return conditionOne && HAS_EMAIL_DRAFTS_INTERNAL(currentConstituent)
}

export const GROUP_EMAIL_ENABLED_OR_ADMIN_AND_EMAIL_DRAFTS = (
  customer,
  currentUser,
  organizations,
  showAdminView,
  currentConstituent,
) => {
  const conditionOne = GROUP_EMAIL_ENABLED_OR_ADMIN(
    customer,
    currentUser,
    organizations,
    showAdminView,
    currentConstituent,
  )
  return conditionOne && HAS_EMAIL_DRAFTS_INTERNAL(currentConstituent)
}

export const NOT_GROUP_EMAIL_ENABLED = (
  customer,
  currentUser,
  _organizations,
  _showAdminView,
  currentConstituent,
) => !getGroupEmail(customer, currentUser, currentConstituent)

export const NOT_PRIVACY_LIMIT_LOCATION_DATA_ENABLED = (customer) =>
  !customer.privacyLimitLocationDataEnabled
export const ALLOW_PRIVACY_CONTROLS_ENABLED = (customer) => customer.allowPrivacyControlsEnabled
export const ADS_ENABLED = (customer) => customer.adsEnabled
export const HOMEPAGE_AD_ENABLED = (customer) => customer.adHomepageEnabled
export const BANNER_AD_ENABLED = (customer) => customer.adBannerEnabled

export const HAS_MULTIDIRECTORY = (_customer, _currentUser, organizations) =>
  organizations && organizations.length > 1

export const HAS_INTERNAL_ACCESS = (customer, currentUser) =>
  currentUser?.wavelengthEmployee && customer.isRoot

export const HAS_CUSTOMER_LEVEL_INTERNAL_ACCESS = (_customer, currentUser) =>
  currentUser?.wavelengthEmployee

export const ZOOM_ENABLED = (customer) => customer?.zoomEnabled
export const FIND_EMAILS_ENABLED = (customer) => customer?.findEmailsEnabled
export const EMAIL_HISTORY_ENABLED = (customer) => customer?.emailHistoryEnabled
export const MARKETING_ENABLED = (customer) => customer.marketingButtonsEnabled
export const JOB_POSTS_ENABLED = (customer) => customer.jobPostsEnabled
export const CLASS_NOTES_ENABLED = (customer) => customer.classNotesEnabled
export const USAGE_DATA_ENABLED = (customer) => customer.usageDataEnabled
export const PRIVACY_LIMIT_LOCATION_DATA_ENABLED = (customer) =>
  customer.privacyLimitLocationDataEnabled
export const INTERNAL_DONATIONS_ENABLED = (customer) => !customer.hideInternalDonations
export const IS_ROOT = (customer) => customer.isRoot
export const IS_NOT_ROOT = (customer) => !customer.isRoot
export const WEALTH_DATA_ENABLED = (customer) => !!customer.premium
export const IS_NOT_PFARS = (customer) => customer.subdomain !== "pfars"
export const CUSTOM_SIDEBAR_LINK_ENABLED = (customer) =>
  customer.customSidebarLinkLocation && customer.customSidebarLinkDisplayText
export const CONTENT_PAGE_ENABLED = (customer) => customer.showContentPage

export const ALWAYS_ALLOW = () => true

// Be aware that PrivateRoute has a function that determines whether a route is shown
// If you add fields to Customer Permissions, you need to add there as well

function CustomerPermissions({ children, allowedIf }) {
  const customer = useCustomer()
  const currentUser = useCurrentUser()
  const currentConstituent = useCurrentConstituent()
  const organizations = useAppSelector((state) => state.navigation.user.organizations)
  const showAdminView = useShowAdminView()

  return allowedIf(customer, currentUser, organizations, showAdminView, currentConstituent)
    ? children
    : null
}

export default CustomerPermissions
